import { useMemo, type ReactElement, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ApiClient, ApiRequestError, TokenStore } from '@proctoring/api-client';
import { API_BASE_URL, SESSION_STORAGE_KEY } from './api/config.js';
import { AuthProvider } from './auth/AuthProvider.js';

/**
 * A query client tuned for a dashboard that is read far more often than it changes.
 *
 * Auth failures are never retried: a 401 means the session is gone, and three more
 * attempts only delay the redirect to /login by a few seconds.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          if (error instanceof ApiRequestError) return false;
          return failureCount < 2;
        },
      },
      mutations: { retry: false },
    },
  });
}

export function createApiClient(tokens?: TokenStore): ApiClient {
  const store =
    tokens ??
    new TokenStore({
      storageKey: SESSION_STORAGE_KEY,
      storage: globalThis.localStorage,
    });
  return new ApiClient({
    baseUrl: API_BASE_URL,
    tokens: store,
    audience: 'portal',
    // Clearing the store is what turns an expired token into a trip to /login. Without it a
    // token that lapses while the tab sits open leaves every query failing with "your session
    // has expired" and no way forward but a manual reload — for a candidate a few minutes
    // before their interview, a dead end. `AuthProvider` subscribes to the store and flips to
    // anonymous when it empties. A failed anonymous login does not reach here.
    onUnauthorized: () => {
      store.clear();
    },
  });
}

export interface AppProvidersProps {
  api?: ApiClient;
  queryClient?: QueryClient;
  children: ReactNode;
}

/**
 * Composes the query client and auth provider. Both dependencies are injectable so tests
 * can mount real pages against a mocked API and a fresh cache.
 */
export function AppProviders({ api, queryClient, children }: AppProvidersProps): ReactElement {
  const client = useMemo(() => queryClient ?? createQueryClient(), [queryClient]);
  const apiClient = useMemo(() => api ?? createApiClient(), [api]);

  return (
    <QueryClientProvider client={client}>
      <AuthProvider api={apiClient}>{children}</AuthProvider>
    </QueryClientProvider>
  );
}
